import { useMemo } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { useUserPreferences } from '../../shared/user-preferences/hooks/useUserPreferences';
import {
  PreferenceScope,
  type UserPreferences,
} from '../../shared/user-preferences/types';
import type { GlobalAssistantEntityType } from './global-ai-assistant-api';

/**
 * Taxonomy used by the global assistant when resolving route filters via
 * `aiFilterSearchRequest`. Mirrors the string format
 * `getRoleTaxonomyIdForAiSearch` produces from a CustomDash filter map,
 * but sourced from user preferences — so the drawer (which lives outside
 * CustomDash) can call the AI filter endpoint without depending on the
 * target page's filter context.
 *
 * IMPORTANT: taxonomies are stored per-scope (COMPANY / GEOGRAPHY / ROLE),
 * not GLOBAL. The scope must be picked from the entity type the AI
 * response is about — `entityType: 'company'` → `PreferenceScope.COMPANY`,
 * etc. Reading `PreferenceScope.GLOBAL` returns empty for most users and
 * causes the endpoint to fall back to generic defaults.
 *
 * React constraint: the drawer doesn't know which entity the user will
 * click in advance, so we subscribe to all three scopes (COMPANY /
 * GEOGRAPHY / ROLE) up-front and return a lookup. React-Query dedupes
 * under a single query key, so three hook calls = one network request.
 */
/**
 * Narrow shape: just the values sent to the AI filter search API. The
 * bridge's `publishActiveTaxonomy` and the store's `activeTaxonomyByEntity`
 * deal in this shape — they mirror the page's `activeFilterMap` without
 * knowledge of user-preference identity.
 */
export type AssistantTaxonomyPayload = {
  rolesTax: string;
  skillsTax: number;
};

/**
 * Full shape including cache-key identity. Used wherever caching matters
 * (the prefetch module, the page-stream cache). The drawer composes this
 * by taking the payload from the bridge-published store value (or from
 * saved prefs when absent) and the keys from saved prefs.
 */
export type AssistantTaxonomy = AssistantTaxonomyPayload & {
  /**
   * Cache-key identity for the role taxonomy. Equals the literal string
   * `'adaptive'` when the user's role preference is adaptive; otherwise
   * equals `rolesTax`. Using preference identity instead of the resolved
   * numeric stops the cache from growing an entry per transient adaptive
   * re-resolution (user's Digital Commerce → k10 partial → k50 full all
   * collapse into one key for the same prompt).
   */
  rolesKey: string;
  /** See `rolesKey` — same idea, for skill. */
  skillsKey: string;
};

const DEFAULT_ROLE_TAX = 'custom_role_default';

function customRoleSelectionToTaxId(
  selection: CustomRoleTaxonomySelection | undefined
): string {
  if (!selection) return DEFAULT_ROLE_TAX;
  // Types allow `{ default?: boolean, ricsK50Id?, ricsK10Id? }` — mirror
  // `getRoleTaxonomyIdForAiSearch` in CustomDash/filters/ai-search/.
  const s = selection as {
    default?: boolean;
    ricsK50Id?: string;
    ricsK10Id?: string;
  };
  if (s.default === true) return DEFAULT_ROLE_TAX;
  if (
    typeof s.ricsK50Id === 'string' &&
    s.ricsK50Id.trim() &&
    s.ricsK50Id !== '0'
  ) {
    return `custom_role_rics_k50_${s.ricsK50Id}`;
  }
  if (
    typeof s.ricsK10Id === 'string' &&
    s.ricsK10Id.trim() &&
    s.ricsK10Id !== '0'
  ) {
    return `custom_role_rics_k10_${s.ricsK10Id}`;
  }
  return DEFAULT_ROLE_TAX;
}

function extractTaxonomy(preferences: UserPreferences): AssistantTaxonomy {
  const taxonomies = preferences?.taxonomies;
  const roleIsAdaptive = taxonomies?.role === 'adaptive';
  const skillIsAdaptive = taxonomies?.skill === 'adaptive';

  // Role: prefer the resolved effective selection; fall back to the raw
  // stored pref if it's a concrete object (not 'adaptive').
  const roleSelection: CustomRoleTaxonomySelection | undefined = (() => {
    const effective = taxonomies?.effectiveRoleTaxonomySelection;
    if (effective && typeof effective === 'object') {
      return effective as CustomRoleTaxonomySelection;
    }
    const raw = taxonomies?.role;
    if (raw && typeof raw === 'object') {
      return raw as CustomRoleTaxonomySelection;
    }
    return undefined;
  })();

  // Skill: effective number if present, else the stored numeric pref,
  // else 0 (= server treats as default).
  const skillsTax: number = (() => {
    const effective = taxonomies?.effectiveSkillTaxonomySelection;
    if (typeof effective === 'number' && Number.isFinite(effective)) {
      return effective;
    }
    const raw = taxonomies?.skill;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw;
    }
    return 0;
  })();

  const rolesTax = customRoleSelectionToTaxId(roleSelection);
  return {
    rolesTax,
    skillsTax,
    rolesKey: roleIsAdaptive ? 'adaptive' : rolesTax,
    skillsKey: skillIsAdaptive ? 'adaptive' : String(skillsTax),
  };
}

/**
 * Returns a `(entityType) => AssistantTaxonomy` resolver. Callers invoke
 * it at the moment they know the entity (e.g. after the suggest response
 * identifies the resolved scope, or when the user clicks a page chip).
 *
 * The returned function is memoized on the three underlying preference
 * objects, so callers can safely put it in an effect dep array without
 * causing churn on every render.
 */
export function useGlobalAssistantTaxonomy(): (
  entityType: GlobalAssistantEntityType
) => AssistantTaxonomy {
  const company = useUserPreferences(PreferenceScope.COMPANY);
  const geography = useUserPreferences(PreferenceScope.GEOGRAPHY);
  const role = useUserPreferences(PreferenceScope.ROLE);

  return useMemo(() => {
    const byEntity: Record<GlobalAssistantEntityType, AssistantTaxonomy> = {
      company: extractTaxonomy(company.preferences),
      geography: extractTaxonomy(geography.preferences),
      role: extractTaxonomy(role.preferences),
      // TD global assistant pre-fetch: mirror company-pref taxonomies (TD is multi-entity).
      talentDiscovery: extractTaxonomy(company.preferences),
    };
    return (entityType) => byEntity[entityType];
  }, [company.preferences, geography.preferences, role.preferences]);
}
